// NIFC ArcGIS Online OAuth 2.0 with PKCE.
//
// We authenticate browser-side against NIFC's portal at
// https://nifc.maps.arcgis.com using the standard ArcGIS Identity OAuth
// flow. PKCE (Proof Key for Code Exchange) lets us avoid shipping a
// client secret in the bundle.
//
// Required from the boss after his NIFC AGOL account is approved:
//   1. Sign in at https://nifc.maps.arcgis.com
//   2. Content -> New Item -> Application -> "Other"
//      Title: Fyrefly
//      Redirect URI: https://fyrefly.vercel.app/auth/agol/callback
//                    + http://localhost:5173/auth/agol/callback (dev)
//   3. Copy the App ID (Client ID)
//   4. Set VITE_AGOL_CLIENT_ID in Vercel env vars
//   5. Boss joins the data groups (FireGuard, NIROPS, etc.) inside NIFC AGOL
//   6. Use Settings -> Sign in to NIFC AGOL inside Fyrefly
//
// Until step 4 is done, getClientId() returns null and the UI surfaces
// 'NIFC AGOL not yet configured' on auth-gated rows. No errors thrown.

const AGOL_PORTAL = 'https://nifc.maps.arcgis.com';
const AUTH_ENDPOINT = `${AGOL_PORTAL}/sharing/rest/oauth2/authorize`;
const TOKEN_ENDPOINT = `${AGOL_PORTAL}/sharing/rest/oauth2/token`;
const SIGNOUT_ENDPOINT = `${AGOL_PORTAL}/sharing/rest/oauth2/signout`;

/** Where the OAuth provider should send the user back. */
function redirectUri(): string {
  return `${window.location.origin}/auth/agol/callback`;
}

/**
 * The Vite-bundled NIFC AGOL Client ID. Returns null when not configured
 * (e.g. before the boss's account is approved + the app is registered).
 * Callers must surface a friendly state instead of attempting auth.
 */
export function getClientId(): string | null {
  const id = (import.meta.env.VITE_AGOL_CLIENT_ID as string | undefined)?.trim();
  return id && id.length > 0 ? id : null;
}

export function isConfigured(): boolean {
  return getClientId() !== null;
}

// ---------------------------------------------------------------------------
// PKCE helpers
// ---------------------------------------------------------------------------

function base64UrlEncode(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function generateCodeVerifier(): Promise<string> {
  const buf = new Uint8Array(32);
  crypto.getRandomValues(buf);
  return base64UrlEncode(buf);
}

async function deriveCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

// ---------------------------------------------------------------------------
// Token persistence (sessionStorage during the redirect dance, IndexedDB for
// long-lived storage after the dust settles).
// ---------------------------------------------------------------------------

const PKCE_KEY = 'fyrefly.agol.pkce';
const RETURN_KEY = 'fyrefly.agol.return';
const TOKEN_DB = 'fyrefly';
const TOKEN_STORE = 'kv';
const TOKEN_KV_KEY = 'agol.tokens.v1';

interface TokenSet {
  accessToken: string;
  refreshToken: string | null;
  expiresAt: number; // epoch ms
  username: string | null;
  ssl: boolean;
}

async function openTokenDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(TOKEN_DB, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(TOKEN_STORE)) {
        db.createObjectStore(TOKEN_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function saveTokens(tokens: TokenSet): Promise<void> {
  const db = await openTokenDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(TOKEN_STORE, 'readwrite');
    tx.objectStore(TOKEN_STORE).put(tokens, TOKEN_KV_KEY);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

export async function loadTokens(): Promise<TokenSet | null> {
  try {
    const db = await openTokenDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(TOKEN_STORE, 'readonly');
      const req = tx.objectStore(TOKEN_STORE).get(TOKEN_KV_KEY);
      req.onsuccess = () => { db.close(); resolve((req.result as TokenSet | undefined) ?? null); };
      req.onerror = () => { db.close(); reject(req.error); };
    });
  } catch {
    return null;
  }
}

async function clearTokens(): Promise<void> {
  try {
    const db = await openTokenDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(TOKEN_STORE, 'readwrite');
      tx.objectStore(TOKEN_STORE).delete(TOKEN_KV_KEY);
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); reject(tx.error); };
    });
  } catch { /* best effort */ }
}

// ---------------------------------------------------------------------------
// Public flow
// ---------------------------------------------------------------------------

/**
 * Start the sign-in flow. Redirects the browser to the NIFC AGOL portal.
 * The page that triggers this should expect to be unloaded.
 *
 * Throws if the client ID isn't configured yet — caller should check
 * isConfigured() first and show a friendly empty-state UI.
 */
export async function startSignIn(returnTo: string = window.location.pathname): Promise<void> {
  const clientId = getClientId();
  if (!clientId) {
    throw new Error('NIFC AGOL OAuth is not configured (missing VITE_AGOL_CLIENT_ID).');
  }
  const verifier = await generateCodeVerifier();
  const challenge = await deriveCodeChallenge(verifier);

  // Stash PKCE verifier + the page we want to return to.
  sessionStorage.setItem(PKCE_KEY, verifier);
  sessionStorage.setItem(RETURN_KEY, returnTo);

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri(),
    response_type: 'code',
    code_challenge: challenge,
    code_challenge_method: 'S256',
    expiration: '20160', // 2-week refresh window in minutes
  });

  window.location.assign(`${AUTH_ENDPOINT}?${params.toString()}`);
}

/**
 * Called by the /auth/agol/callback route. Exchanges the auth code for
 * tokens and persists them. Returns the post-redirect path the caller
 * should navigate to.
 */
export async function handleCallback(): Promise<string> {
  const clientId = getClientId();
  if (!clientId) throw new Error('NIFC AGOL OAuth is not configured.');

  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');
  const error = url.searchParams.get('error');
  if (error) throw new Error(`AGOL auth error: ${error} ${url.searchParams.get('error_description') ?? ''}`);
  if (!code) throw new Error('Missing auth code on callback.');

  const verifier = sessionStorage.getItem(PKCE_KEY);
  if (!verifier) throw new Error('Missing PKCE verifier — sign-in flow was lost.');

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: clientId,
    code,
    redirect_uri: redirectUri(),
    code_verifier: verifier,
  });

  const r = await fetch(TOKEN_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!r.ok) throw new Error(`AGOL token exchange ${r.status}`);
  const data = await r.json();

  if (data.error) throw new Error(`AGOL token error: ${data.error}`);

  const tokens: TokenSet = {
    accessToken: data.access_token,
    refreshToken: data.refresh_token ?? null,
    expiresAt: Date.now() + Number(data.expires_in ?? 0) * 1000,
    username: data.username ?? null,
    ssl: Boolean(data.ssl),
  };
  await saveTokens(tokens);

  const returnTo = sessionStorage.getItem(RETURN_KEY) || '/';
  sessionStorage.removeItem(PKCE_KEY);
  sessionStorage.removeItem(RETURN_KEY);
  return returnTo;
}

/**
 * Get a valid access token, refreshing from refresh_token when needed.
 * Returns null if we don't have a usable session.
 */
export async function getAccessToken(): Promise<string | null> {
  const tokens = await loadTokens();
  if (!tokens) return null;
  // 60-second skew buffer.
  if (tokens.expiresAt - 60_000 > Date.now()) return tokens.accessToken;
  if (!tokens.refreshToken) return null;

  const clientId = getClientId();
  if (!clientId) return null;

  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: clientId,
    refresh_token: tokens.refreshToken,
  });
  const r = await fetch(TOKEN_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!r.ok) return null;
  const data = await r.json();
  if (data.error || !data.access_token) return null;

  const next: TokenSet = {
    ...tokens,
    accessToken: data.access_token,
    expiresAt: Date.now() + Number(data.expires_in ?? 0) * 1000,
    // ArcGIS occasionally rotates the refresh_token — keep the new one if present.
    refreshToken: data.refresh_token ?? tokens.refreshToken,
  };
  await saveTokens(next);
  return next.accessToken;
}

export async function getCurrentUsername(): Promise<string | null> {
  const t = await loadTokens();
  return t?.username ?? null;
}

export async function isSignedIn(): Promise<boolean> {
  const t = await getAccessToken();
  return !!t;
}

/**
 * Sign out: clears local tokens and pings the AGOL signout endpoint as a
 * best-effort. Idempotent.
 */
export async function signOut(): Promise<void> {
  await clearTokens();
  try {
    // Best effort — failure here doesn't block the user being signed out locally.
    await fetch(SIGNOUT_ENDPOINT, { method: 'GET', mode: 'no-cors' });
  } catch { /* ignore */ }
}
